import { Pie, PieChart, Cell, ResponsiveContainer } from 'recharts';
import { DataTable } from '@/components/ui/data-table';
import type { DataTableColumn } from '@/components/ui/data-table';
import { chartColorArray } from '@/lib/colors';
import { ProviderIcon } from '@/components/provider-pill';
import { Skeleton } from '@/components/ui/skeleton';
import { CurrencyValue } from '@/components/CurrencyValue';
import type { ProvidersBreakdownResponse } from '@/data/types/dashboard.types';
import { DashboardEmptyState } from '@/components/Dashboard/EmptyState';

// Props interface for the component
interface ProvidersBreakdownProps {
  data?: ProvidersBreakdownResponse | null;
  isLoading?: boolean;
}

// Extract provider from model name (fallback function)
const getProviderFromModel = (model: string, provider?: string): string => {
  if (provider && provider !== 'others') {
    return provider.toLowerCase();
  }
  const modelLower = model.toLowerCase();
  if (modelLower.includes('gpt') || modelLower.includes('openai')) {
    return 'openai';
  }
  if (modelLower.includes('gemini') || modelLower.includes('google')) {
    return 'gemini';
  }
  if (modelLower.includes('claude') || modelLower.includes('anthropic')) {
    return 'claude';
  }
  return 'others';
};

// Data item type
type DataItem = {
  model: string;
  provider: string;
  cost: number;
  color: string;
  costPerAction: number;
  costDistribution: number;
  isTotal?: boolean;
};

// Custom label component for pie chart with percentage and line
const renderCustomLabel = (props: any) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent, payload } = props;
  
  // Show label for all segments (no minimum threshold to ensure green segment is visible)
  if (percent < 0.001) return null;

  const RADIAN = Math.PI / 180;

  // Calculate the midpoint of the segment
  const radius = innerRadius + (outerRadius - innerRadius) * 0.7;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  // Calculate position for label just outside the ring
  const outerRadiusValue = typeof outerRadius === 'number'
    ? outerRadius
    : (Math.min(cx, cy));
  // Make the line short (8px outside ring)
  const labelRadius = outerRadiusValue + 20; 
  const labelX = cx + labelRadius * Math.cos(-midAngle * RADIAN);
  const labelY = cy + labelRadius * Math.sin(-midAngle * RADIAN);

  const percentage = (percent * 100).toFixed(1);
  const modelName = payload?.name || '';
  const provider = payload?.provider || '';

  // Determine text anchor based on position
  const isRightSide = labelX > cx;

  // Get provider for icon
  const providerName = getProviderFromModel(modelName, provider);

  // Calculate width needed for the label (approximate based on text length)
  const estimatedWidth = Math.max(120, modelName.length * 7 + 50);

  return (
    <g>
      {/* Short line from segment to label */}
      <line
        x1={x}
        y1={y}
        x2={labelX}
        y2={labelY}
        stroke="#8A8F98"
        strokeWidth={1}
        strokeDasharray="2,2"
      />
      {/* Use foreignObject to render React component (icon) */}
      <foreignObject
        x={isRightSide ? labelX : labelX - estimatedWidth}
        y={labelY - 8}
        width={estimatedWidth}
        height={16}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            fontWeight: 500,
            color: '#08090A',
            justifyContent: isRightSide ? 'flex-start' : 'flex-end',
            whiteSpace: 'nowrap',
          }}
        >
          <ProviderIcon provider={providerName} className="w-3.5 h-3.5 shrink-0" />
          <span>
            {modelName} ({percentage}%)
          </span>
        </div>
      </foreignObject>
    </g>
  );
};

const ProvidersBreakdown = ({ data: apiData, isLoading = false }: ProvidersBreakdownProps) => {
  // Transform API data to internal format with colors
  const processedData: DataItem[] = apiData?.items.map((item, index) => ({
    model: item.model,
    provider: item.provider,
    cost: item.total_costs,
    costPerAction: item.cost_per_action,
    costDistribution: item.cost_distribution,
    color: chartColorArray[index % chartColorArray.length],
    isTotal: false,
  })) || [];

  const totals = apiData?.totals;

  // Prepare chart data
  const chartData = processedData.map((item) => ({
    name: item.model,
    value: item.cost,
    fill: item.color,
    provider: item.provider,
    percentage: item.costDistribution,
  }));

  // Add total row
  const tableDataWithTotal: DataItem[] = [
    ...processedData,
    ...(totals ? [{
      model: 'total',
      provider: 'total',
      cost: totals.total_costs,
      costPerAction: totals.cost_per_action,
      costDistribution: totals.cost_distribution,
      color: chartColorArray[6],
      isTotal: true,
    }] : []),
  ];

  // Define columns
  const columns: DataTableColumn<DataItem>[] = [
    {
      id: 'model',
      header: 'Model',
      flex: 1.2,
      align: 'left',
      enableSorting: false,
      cell: ({ row }) => {
        const item = row.original;
        if (item.isTotal) {
          return <span className="font-bold">Total</span>;
        }
        
        const provider = getProviderFromModel(item.model, item.provider);
        
        return (
          <div className="flex items-center gap-[8px]">
            <div 
              className="size-2 rounded-full shrink-0" 
              style={{ backgroundColor: item.color }}
            />
            <ProviderIcon provider={provider} className="w-4 h-4" />
            <span className="font-medium">{item.model}</span>
          </div>
        );
      },
    },
    {
      id: 'costs',
      header: 'Total Costs',
      accessorKey: 'cost',
      flex: 0.8,
      align: 'left',
      enableSorting: false,
      cell: ({ row, getValue }) => {
        const value = getValue() as number;
        return (
          <CurrencyValue value={value} className={row.original.isTotal ? 'font-bold' : ''} />
        );
      },
    },
    {
      id: 'costPerAction',
      header: '$ per Action',
      accessorKey: 'costPerAction',
      flex: 0.7,
      align: 'left',
      enableSorting: false,
      cell: ({ row, getValue }) => {
        const value = getValue() as number;
        return (
          <CurrencyValue value={value} className={row.original.isTotal ? 'font-bold' : ''} />
        );
      },
    },
    {
      id: 'costDistribution',
      header: 'Cost Distribution',
      accessorKey: 'costDistribution',
      flex: 1,
      align: 'left',
      enableSorting: false,
      cell: ({ row, getValue }) => {
        const value = getValue() as number;
        return (
          <span className={row.original.isTotal ? 'font-bold' : ''}>
            {value.toFixed(1)}%
          </span>
        );
      },
    },
  ];

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="w-full flex justify-center" style={{ height: '220px', padding: '15px 0' }}>
          <Skeleton className="w-[180px] h-[180px] rounded-full" />
        </div>
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-[40px] w-full rounded-[6px]" />
          ))}
        </div>
      </div>
    );
  }

  // Check if all values are effectively zero/empty
  const hasData = apiData && processedData.length > 0 && 
    processedData.some(item => item.cost > 0);

  // Empty state
  if (!hasData) {
    return <DashboardEmptyState height={300} />;
  }
  
  return (
    <div className="space-y-4">
      {/* Ring Chart */}
      <div className="w-full flex justify-center" style={{ height: '220px', padding: '15px 0' }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <Pie
              data={chartData}
              startAngle={90}
              endAngle={-270}
              innerRadius="60%"
              outerRadius="85%"
              cornerRadius={5}
              paddingAngle={2}
              cx="50%"
              cy="50%"
              dataKey="value"
              isAnimationActive={true}
              stroke="none"
              label={renderCustomLabel}
              labelLine={false}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <DataTable
        data={tableDataWithTotal}
        columns={columns}
        enableColumnResizing={false}
        enableSorting={false}
        getRowId={(row) => row.model}
        getRowClassName={(row) => 
          row.isTotal 
            ? 'font-bold' 
            : ''
        }
      />
    </div>
  );
};

export { ProvidersBreakdown };

